/*
 * InfoHint — an "i" that holds the explanation until it is asked for.
 *
 * A paragraph of guidance under a control is read once and skipped forever
 * after, while permanently costing the form a line and pushing the actual
 * fields down. Behind an icon it stays one keystroke or one hover away and the
 * form reads as a form.
 *
 * The bubble is portalled and fixed-positioned, so it is never clipped by a
 * drawer, a card's overflow, or a table cell.
 */

.info-hint {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

.info-hint__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-full);
  background: var(--accent-soft);
  color: var(--accent-ink);
  /* 'help', not 'pointer': it reveals an explanation, it does not navigate. */
  cursor: help;
  transition: background var(--transition-fast), color var(--transition-fast);
}

/* A small soft disc in the identity (2026-10-22): present enough to be noticed beside
   a title, quiet enough not to compete with it. Asked, it deepens — no bounce. */
.info-hint__trigger:hover,
.info-hint__trigger[aria-expanded='true'] {
  background: color-mix(in srgb, var(--accent) 22%, var(--bg-surface));
}

.info-hint__trigger:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* Beside a field's 12px label — often riding on the field's edge — a smaller disc, so
   the label keeps its own height. */
.form-row__label .info-hint__trigger {
  width: 16px;
  height: 16px;
}

.form-row__label .info-hint__trigger svg {
  width: 11px;
  height: 11px;
}

/* ---- The bubble: a light note (2026-10-22) ----
   White, a soft hairline and a gentle shadow; it opens with the same soft "i" mark that
   summoned it, and the text sits beside it a step under body size, with room between
   the lines. No accent stripe and no dark slab. */

.info-hint__tip {
  position: fixed;
  z-index: var(--z-tooltip);
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  max-width: 300px;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--bg-elevated);
  color: var(--text);
  box-shadow: var(--shadow-raised);
  font-weight: var(--weight-regular);
  text-align: start;
  white-space: normal;
  pointer-events: none;
  animation: info-hint-in var(--transition-fast);
}

.info-hint__tip-mark {
  display: grid;
  place-items: center;
  flex: none;
  width: 22px;
  height: 22px;
  border-radius: var(--radius-full);
  background: var(--accent-soft);
  color: var(--accent-ink);
}

.info-hint__tip-text {
  min-width: 0;
  padding-block-start: 1px;
  font-size: var(--text-xs);
  line-height: 1.75;
  text-wrap: pretty;
}

/* 'left' / 'top' are the icon's centre, set inline; the transform centres the
   bubble on that point and seats it above or below. Direction-neutral, so RTL
   needs no rule of its own. */
.info-hint__tip--above {
  transform: translate(-50%, calc(-100% - 10px));
  transform-origin: bottom center;
}

.info-hint__tip--below {
  transform: translate(-50%, 10px);
  transform-origin: top center;
}

/* The caret: a square of the bubble's own surface and hairline, turned to a point. */
.info-hint__tip::after {
  content: '';
  position: absolute;
  inset-inline-start: 50%;
  width: 10px;
  height: 10px;
  margin-inline-start: -5px;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  transform: rotate(45deg);
}

.info-hint__tip--above::after {
  inset-block-end: -6px;
  border-block-start: 0;
  border-inline-start: 0;
}

.info-hint__tip--below::after {
  inset-block-start: -6px;
  border-block-end: 0;
  border-inline-end: 0;
}

@keyframes info-hint-in {
  from {
    opacity: 0;
    scale: 0.96;
  }
}
